/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import { Tabs, Spin, Empty } from 'antd';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';
import moment from 'moment';
import dompurify from 'dompurify';
import { CalendarOutlined, ShoppingOutlined, SearchOutlined, StarOutlined, RightOutlined, UserOutlined, MobileOutlined } from '@ant-design/icons';
import SubscriptionButton from '../../components/SubscriptionButton';
import Accordion from '../../components/Accordion';
import { getHoroscopeDataRange, getSelectedTranslation, getTranslation } from '../../core/utils/helpers';
import Main from '../../components/layout/Main';
import SEO from '../../components/SEO';
import { PAGE_SEO_CONFIG } from '../../components/SEO/seoConfig';
import bellBadgedImg from '../../assets/images/clarity_bell-solid-badged.svg';
import bellImg from '../../assets/images/clarity_bell-solid.svg';
import YellowThemeButton from '../../components/YellowThemeButton';
import Logo from '../../components/Logo';
import app from '../../core/constants';

// Icons for quick access cards

import {
    userActiveHoroscopesRequest,
    selectSingleHoroscopeRequest,
    checkIfLiveRequest,
} from '../../core/horoscope/horoscopeActions';

import {
    getUserDetailsRequest,
} from '../../core/user/userActions';

import {
    noticeboardRequest,
} from '../../core/noticeboard/noticeboardActions';

import './dashboard.scss';

export const WEEKLY_TAB_ID = '1';
export const MONTHLY_TAB_ID = '2';
export const YEARLY_TAB_ID = '3';

const { TabPane } = Tabs;
const Dashboard = () => {
    const user = useSelector(state => state.user);
    const dispatch = useDispatch();
    const history = useHistory();
    const horoscope = useSelector(state => state.horoscope);
    const language = useSelector(state => state.language);
    const isUserSubscribed = user?.userData?.active_subscriptions?.some(subscription => subscription.status === 'active') || false;
    const noticeboard = useSelector(state => state.noticeboard);
    const [selectedTab, setSelectedTab] = useState(WEEKLY_TAB_ID);
    const userMoonsignKey = user?.userData?.generated_horoscope?.key?.toLowerCase();

    const location = useLocation();

    useEffect(() => {
        dispatch(userActiveHoroscopesRequest());
        dispatch(getUserDetailsRequest());
        dispatch(checkIfLiveRequest());
        dispatch(noticeboardRequest());
    }, []);

    useEffect(() => {
        const redirectUrl = window.sessionStorage.getItem('deepLinkRedirect');
        if (redirectUrl) {
            window.sessionStorage.removeItem('deepLinkRedirect');
            history.push(redirectUrl);
        }
    }, [history]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        if (tab) {
            setSelectedTab(tab);
        } else {
            // Check if we have a saved tab in sessionStorage
            const savedTab = typeof window !== 'undefined' ? window.sessionStorage.getItem('dashboardTab') : null;
            if (savedTab) {
                setSelectedTab(savedTab);
            } else {
                setSelectedTab(WEEKLY_TAB_ID);
            }
        }
    }, [location.search]);

    const getCurrentHoroscopeTimePeriod = (currentHoroscope) => {
        if (currentHoroscope?.current_period.cycle === 1) {
            return 'weekly';
        }
        if (currentHoroscope?.current_period.cycle === 2) {
            return 'monthly';
        }
        return 'yearly';
    };

    const selectSingleHoroscope = (currentHoroscope) => {
        const timePeriod = getCurrentHoroscopeTimePeriod(currentHoroscope);
        const horoscopeKey = currentHoroscope?.key?.toLowerCase();
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('dashboardTab', selectedTab);
        }
        history.push(`/horoscope/${timePeriod}/${horoscopeKey}`);
    };

    const selectingLifeEvents = (currentHoroscope) => {
        dispatch(selectSingleHoroscopeRequest(currentHoroscope));
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('dashboardTab', selectedTab);
        }
        const horoscopeSign = currentHoroscope?.key?.toLowerCase();
        history.push(`/life-events/${horoscopeSign}`);
    };

    const handleTabChange = (key) => {
        // setSelectedTab(key);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('tab', key);
        history.push({ search: searchParams.toString() });
    };

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];
    const menu = [{ label: 'Item 1', link: '/abc', iconName: 'home', style: { fontSize: 24, color: 'red' } }];

    // Get time-based greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return getTranslation(language, 'greeting_morning', 'Good morning');
        if (hour < 17) return getTranslation(language, 'greeting_afternoon', 'Good afternoon');
        return getTranslation(language, 'greeting_evening', 'Good evening');
    };

    // Get user's first name
    const getFirstName = () => {
        const fullName = user?.userData?.full_name || '';
        return fullName.split(' ')[0] || getTranslation(language, 'greeting_default_name', 'there');
    };

    // Render welcome section
    const renderWelcomeSection = () => (
        <div className="welcome-section">
            <h1 className="welcome-greeting">
                {getGreeting()}, {getFirstName()}!
            </h1>
            <p className="welcome-subtitle">
                {getTranslation(language, 'welcome_subtitle', "Here's your cosmic forecast")}
            </p>
        </div>
    );

    // Render live show banner (when streaming is active)
    const renderLiveBanner = () => {
        const liveData = horoscope?.horocopeStreamingLiveData;
        if (!liveData || liveData.isLive !== 1) return null;

        return (
            <a
                href={liveData.url}
                target="_blank"
                rel="noreferrer"
                className="live-banner">
                <div className="live-indicator">
                    <span className="live-dot" />
                    <span className="live-text">LIVE</span>
                </div>
                <span className="live-message">
                    {getTranslation(language, 'live_streaming_now', 'Prakash is streaming now!')}
                </span>
                <span className="watch-now">
                    {getTranslation(language, 'watch_live', 'Watch Live')} →
                </span>
            </a>
        );
    };

    // Render quick access cards
    const renderQuickAccessCards = () => (
        <div className="quick-access-section">
            <h2 className="section-title">
                {getTranslation(language, 'explore_services', 'Explore Our Services')}
            </h2>
            <div className="quick-access-grid">
                <Link to="/consultations" className="quick-access-card">
                    <CalendarOutlined className="card-icon" />
                    <h3>{getTranslation(language, 'consultations_title', 'Consultations')}</h3>
                    <p>{getTranslation(language, 'consultations_desc', 'Book a personal reading with Prakash')}</p>
                </Link>
                <Link to="/gemstones" className="quick-access-card">
                    <ShoppingOutlined className="card-icon" />
                    <h3>{getTranslation(language, 'gemstones_title', 'Gemstones')}</h3>
                    <p>{getTranslation(language, 'gemstones_desc', 'Discover your lucky stones')}</p>
                </Link>
                <Link to="/moonsign-lookup" className="quick-access-card">
                    <SearchOutlined className="card-icon" />
                    <h3>{getTranslation(language, 'moonsign_lookup_title', 'Moonsign Lookup')}</h3>
                    <p>{getTranslation(language, 'moonsign_lookup_desc', 'Find your moon sign')}</p>
                </Link>
                <Link to="/rising-sign-lookup" className="quick-access-card">
                    <SearchOutlined className="card-icon" />
                    <h3>{getTranslation(language, 'rising_sign_lookup_title', 'Rising Sign')}</h3>
                    <p>{getTranslation(language, 'rising_sign_lookup_desc', 'Discover your ascendant')}</p>
                </Link>
            </div>
        </div>
    );

    // Render notice highlights
    const renderNoticeHighlights = () => {
        const notices = noticeboard?.noticeboardData;
        if (!notices || !Array.isArray(notices) || notices.length === 0) return null;

        const recentNotices = notices.slice(0, 3);

        return (
            <div className="notice-highlights-section">
                <div className="section-header">
                    <h2 className="section-title">
                        {getTranslation(language, 'notices_title', 'Notices')}
                    </h2>
                    <Link to="/noticeboard" className="view-all-link">
                        {getTranslation(language, 'view_all', 'View All')}
                    </Link>
                </div>
                <div className="notice-list">
                    {recentNotices.map((notice) => {
                        const noticeTranslation = getSelectedTranslation(notice?.translations, language?.selectedLanguage?.id);
                        const isUnread = !notice.read_at;
                        return (
                            <Link
                                key={notice.id}
                                to={`/noticeboard/${notice.id}`}
                                className={`notice-item ${isUnread ? 'unread' : ''}`}>
                                {isUnread && <span className="unread-dot" />}
                                <span className="notice-title">{noticeTranslation?.title || notice.title || notice.name}</span>
                                <span className="notice-date">
                                    {moment(notice.created_at).format('DD/MM/YYYY')}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Render Today's Horoscope highlight card
    const renderTodaysHoroscope = () => {
        const weeklyHoroscopes = horoscope?.userActiveHoroscopes?.Weekly;
        if (!weeklyHoroscopes || weeklyHoroscopes.length === 0) return null;

        // Find the horoscope matching the user's generated moonsign instead of blindly using [0]
        const primaryHoroscope = (userMoonsignKey
            && weeklyHoroscopes.find(h => h?.horoscope?.key?.toLowerCase() === userMoonsignKey))
            || weeklyHoroscopes[0];
        const data = primaryHoroscope?.horoscope;
        if (!data) return null;

        const horoscopeTranslation = getSelectedTranslation(data?.translations, language?.selectedLanguage?.id);
        const horoscopeDataTranslation = getSelectedTranslation(data?.horoscopeData?.translations, language?.selectedLanguage?.id);
        const horoscopeKey = data?.key?.toLowerCase();

        return (
            <div className="todays-horoscope-section">
                <Link to={`/horoscope/weekly/${horoscopeKey}`} className="todays-horoscope-card">
                    <div className="horoscope-card-content">
                        <div className="horoscope-card-image">
                            <img src={data?.mediaUrl} alt={horoscopeTranslation?.name} />
                        </div>
                        <div className="horoscope-card-info">
                            <span className="horoscope-badge">
                                {getTranslation(language, 'todays_horoscope', "Today's Horoscope")}
                            </span>
                            <h3 className="horoscope-card-title">{horoscopeTranslation?.name}</h3>
                            <p className="horoscope-card-preview">
                                {horoscopeDataTranslation?.short_description?.substring(0, 100)}...
                            </p>
                            <span className="read-now-link">
                                {getTranslation(language, 'read_now', 'Read Now')} <RightOutlined />
                            </span>
                        </div>
                    </div>
                </Link>
            </div>
        );
    };

    // Render Special Forecasts preview (monthly + yearly)
    const renderSpecialForecasts = () => {
        const sanitizer = dompurify.sanitize;
        const groups = [];

        // Collect monthly special forecasts
        const monthlyHoroscopes = horoscope?.userActiveHoroscopes?.Monthly;
        if (monthlyHoroscopes && monthlyHoroscopes.length > 0) {
            const primaryMonthly = (userMoonsignKey
                && monthlyHoroscopes.find(h => h?.horoscope?.key?.toLowerCase() === userMoonsignKey))
                || monthlyHoroscopes[0];
            const monthlyData = primaryMonthly?.horoscope?.horoscopeData;
            const monthlyForecasts = monthlyData?.specialForecasts || monthlyData?.special_forecasts || [];
            if (monthlyForecasts.length > 0) {
                groups.push({ label: getTranslation(language, 'monthly_special_forecasts', 'Monthly Special Forecasts'), forecasts: monthlyForecasts });
            }
        }

        // Collect yearly special forecasts
        const yearlyHoroscopes = horoscope?.userActiveHoroscopes?.Yearly;
        if (yearlyHoroscopes && yearlyHoroscopes.length > 0) {
            const primaryYearly = (userMoonsignKey
                && yearlyHoroscopes.find(h => h?.horoscope?.key?.toLowerCase() === userMoonsignKey))
                || yearlyHoroscopes[0];
            const yearlyData = primaryYearly?.horoscope?.horoscopeData;
            const yearlyForecasts = yearlyData?.specialForecasts || yearlyData?.special_forecasts || [];
            if (yearlyForecasts.length > 0) {
                groups.push({ label: getTranslation(language, 'yearly_special_forecasts', 'Yearly Special Forecasts'), forecasts: yearlyForecasts });
            }
        }

        if (groups.length === 0) return null;

        return (
            <div className="special-forecasts-section">
                <h2 className="section-title">
                    {getTranslation(language, 'special_forecasts', 'Special Forecasts')}
                </h2>
                {groups.map((group) => (
                    <div key={group.label} className="special-forecasts-group">
                        <h3 className="special-forecasts-group-label">{group.label}</h3>
                        <div className="special-forecasts-list">
                            {group.forecasts.map((forecast) => {
                                const forecastTranslation = getSelectedTranslation(forecast?.translations, language?.selectedLanguage?.id);
                                return (
                                    <Accordion
                                        key={forecast.id}
                                        title={forecastTranslation?.name || forecast.name || 'Forecast'}>
                                        {forecastTranslation?.description && (
                                            <div
                                                className="forecast-description"
                                                dangerouslySetInnerHTML={{ __html: sanitizer(forecastTranslation.description) }} />
                                        )}
                                    </Accordion>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render Life Events CTA
    const renderLifeEventsCTA = () => {
        const yearlyHoroscopes = horoscope?.userActiveHoroscopes?.Yearly;
        if (!yearlyHoroscopes || yearlyHoroscopes.length === 0) return null;

        const primaryYearly = (userMoonsignKey
            && yearlyHoroscopes.find(h => h?.horoscope?.key?.toLowerCase() === userMoonsignKey))
            || yearlyHoroscopes[0];
        const data = primaryYearly?.horoscope;
        if (!data) return null;

        const horoscopeKey = data?.key?.toLowerCase();
        const hasAccess = app.ENABLE_FREE_PREMIUM || data.is_user_subscribed || data.has_one_off_purchase;

        return (
            <div className="life-events-cta-section">
                <Link to={`/life-events/${horoscopeKey}`} className="life-events-cta-card">
                    <div className="cta-icon">
                        <StarOutlined />
                    </div>
                    <div className="cta-content">
                        <h3>{getTranslation(language, 'life_events_title', 'Life Events')}</h3>
                        <p>
                            {hasAccess
                                ? getTranslation(language, 'life_events_cta_subscribed', 'Explore your detailed yearly forecasts and life predictions')
                                : getTranslation(language, 'life_events_cta', 'Unlock detailed yearly forecasts and life predictions')}
                        </p>
                    </div>
                    <div className="cta-arrow">
                        <RightOutlined />
                    </div>
                </Link>
            </div>
        );
    };

    // Render Android App Download Banner
    const renderAndroidAppBanner = () => (
        <div className="android-app-section">
            <a
                href="https://play.google.com/store/apps/details?id=com.prakash.astrology"
                target="_blank"
                rel="noopener noreferrer"
                className="android-app-banner">
                <div className="banner-icon">
                    <MobileOutlined />
                </div>
                <div className="banner-content">
                    <h3>{getTranslation(language, 'get_android_app', 'Get the Android App')}</h3>
                    <p>{getTranslation(language, 'android_app_desc', 'Download for the best mobile experience')}</p>
                </div>
                <div className="banner-badge">
                    <span className="play-store-text">Google Play</span>
                </div>
            </a>
        </div>
    );

    // Render Profile Quick Access
    const renderProfileQuickAccess = () => {
        const firstName = getFirstName();
        const userEmail = user?.userData?.email;

        return (
            <div className="profile-quick-access-section">
                <Link to="/profile" className="profile-quick-access-card">
                    <div className="profile-avatar">
                        <UserOutlined />
                    </div>
                    <div className="profile-info">
                        <h3>{firstName || getTranslation(language, 'my_profile', 'My Profile')}</h3>
                        {userEmail && <p className="profile-email">{userEmail}</p>}
                        <span className="profile-link-text">
                            {getTranslation(language, 'view_profile', 'View Profile')} <RightOutlined />
                        </span>
                    </div>
                </Link>
            </div>
        );
    };

    const renderSingleHoroscope = (sTH, cycle) => {
        const data = sTH?.horoscope;

        const horoscopeTranslation = getSelectedTranslation(data?.translations, language?.selectedLanguage?.id);
        const horoscopeDataTranslation = getSelectedTranslation(data?.horoscopeData?.translations, language?.selectedLanguage?.id);
        const userSubscribedClass = (app.ENABLE_FREE_PREMIUM || data.is_user_subscribed) ? 'user-subscribed' : '';

        if (isEmpty(sTH)) {
            return <></>;
        }

        if (cycle === 'yearly') {
            return (
                <div className={`horoscope-item ${userSubscribedClass}`} key={data.id}>
                    <div className="moonsign">
                        <h4 className="horoscope-title">{horoscopeTranslation?.name}</h4>
                        <h5 className="horoscope-subtitle">{getHoroscopeDataRange(data)}</h5>
                    </div>

                    <div className="horoscope-description">
                        <p>{horoscopeDataTranslation?.short_description}</p>
                    </div>
                    <div className="horoscope-image">
                        <img src={data.mediaUrl} alt="moonsign" />
                    </div>
                    <div className="buttons">
                        {data.is_user_subscribed || data.has_one_off_purchase
                            ? (
                                <div className="subscribed-button-wrapper">
                                    <YellowThemeButton type="button" onClick={() => selectingLifeEvents(data)}>{getTranslation(language, 'life_events', 'Life Events')}</YellowThemeButton>
                                </div>
                            ) : (
                                <>
                                    {/* <YellowThemeButton type="button" onClick={() => selectSingleHoroscope(data)}>{getTranslation(language, 'horoscopes', 'Horoscopes')}</YellowThemeButton> */}
                                    <YellowThemeButton type="button" onClick={() => selectingLifeEvents(data)}>{getTranslation(language, 'life_events', 'Life Events')}</YellowThemeButton>
                                </>
                            )}

                    </div>
                </div>
            );
        }

        return (
            <div className={`horoscope-card-item ${userSubscribedClass}`} key={data.id} onClick={() => selectSingleHoroscope(data)}>
                <div className="horoscope-card-content">
                    <div className="horoscope-card-image">
                        <img src={data?.mediaUrl} alt={horoscopeTranslation?.name} />
                    </div>
                    <div className="horoscope-card-info">
                        <h3 className="horoscope-card-title">{horoscopeTranslation?.name}</h3>
                        <p className="horoscope-card-preview">{horoscopeDataTranslation?.short_description}</p>
                        <span className="read-more-link">
                            {getTranslation(language, 'horoscopes_listing_screen_read_more_btn_title', 'Read more')} <RightOutlined />
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <SEO
                title={PAGE_SEO_CONFIG.dashboard.title}
                description={PAGE_SEO_CONFIG.dashboard.description}
                url={PAGE_SEO_CONFIG.dashboard.url}
                noindex={PAGE_SEO_CONFIG.dashboard.noindex} />
            <Main
                headerTitle={<div> <Logo /></div>}
                menu={menu}
                menuWidth={350}
                menuPlacement="right"
                toolbar={toolbar}
                floatingHeader
                showFooter={false}>
                <div className="dashboard-container">
                    <div className="signup-button">

                        <div style={{ width: '100%', textAlign: 'right' }}>
                            {!app.ENABLE_FREE_PREMIUM && !isUserSubscribed ? (
                                <SubscriptionButton selectDefaultUserHoroscope horoscopeContentFrequency={selectedTab} />
                            ) : <></>}
                        </div>

                        <a
                            href="https://play.google.com/store/apps/details?id=com.prakash.astrology"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="android-app-icon">
                            <MobileOutlined />
                        </a>

                        <Link to={{ pathname: '/noticeboard', state: { notices: selectedTab } }} className="bell-icon">
                            {noticeboard.noticeboardDataAllread
                                ? <img src={bellImg} alt="notification all read" />
                                : <img src={bellBadgedImg} alt="notification not read" />}
                        </Link>
                    </div>

                    {/* Welcome Section */}
                    {renderWelcomeSection()}

                    {/* Live Show Banner (conditional) */}
                    {renderLiveBanner()}

                    {/* Today's Horoscope Highlight */}
                    {renderTodaysHoroscope()}

                    <div className="content">
                        <Tabs className="theme-tabs"
                            defaultActiveKey="1"
                            activeKey={selectedTab}
                            onChange={(key) => handleTabChange(key)}>
                            <TabPane tab={getTranslation(language, 'horoscopes_listing_screen_tab_weekly_title', 'Weekly')} key={WEEKLY_TAB_ID}>
                                {horoscope?.isFetchingInitial === true
                                    ? (
                                        <div className="loaderSpacer">
                                            <Spin size="large" />
                                        </div>
                                    )
                                    : (
                                        <div className="horoscope-list">
                                            {!isEmpty(horoscope.userActiveHoroscopes.Weekly) ? horoscope.userActiveHoroscopes.Weekly.map(sTH => renderSingleHoroscope(sTH, 'weekly'))
                                                : <Empty style={{ marginTop: 100 }} description={getTranslation(language, 'horoscopes_listing_screen_no_horoscopes_label', 'There are no horoscopes.')} />}
                                        </div>
                                    )}

                            </TabPane>
                            <TabPane tab={getTranslation(language, 'horoscopes_listing_screen_tab_monthly_title', 'Monthly')} key={MONTHLY_TAB_ID}>
                                {horoscope?.isFetchingInitial === true
                                    ? (
                                        <div className="loaderSpacer">
                                            <Spin size="large" />
                                        </div>
                                    )
                                    : (
                                        <div className="horoscope-list">
                                            {!isEmpty(horoscope.userActiveHoroscopes.Monthly) ? horoscope.userActiveHoroscopes.Monthly.map(sTH => renderSingleHoroscope(sTH, 'monthly'))
                                                : <Empty style={{ marginTop: 100 }} description={getTranslation(language, 'horoscopes_listing_screen_no_horoscopes_label', 'There are no horoscopes.')} />}
                                        </div>
                                    )}

                            </TabPane>
                            {(!horoscope.hasFetchedHorosopes || !isEmpty(horoscope.userActiveHoroscopes.Yearly)) && (
                                <TabPane tab={getTranslation(language, 'horoscopes_listing_screen_tab_yearly_title', 'Yearly & Events')} key={YEARLY_TAB_ID}>
                                    {horoscope?.isFetchingInitial === true
                                        ? (
                                            <div className="loaderSpacer">
                                                <Spin size="large" />
                                            </div>
                                        )
                                        : (
                                            <div className="horoscope-list">
                                                {!isEmpty(horoscope.userActiveHoroscopes?.Yearly) ? horoscope.userActiveHoroscopes.Yearly.map(sTH => renderSingleHoroscope(sTH, 'yearly'))
                                                    : <Empty style={{ marginTop: 100 }} description={getTranslation(language, 'horoscopes_listing_screen_no_horoscopes_label', 'There are no horoscopes.')} />}
                                            </div>
                                        )}

                                </TabPane>
                            )}
                        </Tabs>
                    </div>

                    {/* Life Events CTA */}
                    {renderLifeEventsCTA()}

                    {/* Special Forecasts Preview */}
                    {renderSpecialForecasts()}

                    {/* Quick Access Cards */}
                    {renderQuickAccessCards()}

                    {/* Notice Highlights */}
                    {renderNoticeHighlights()}

                    {/* Profile Quick Access */}
                    {renderProfileQuickAccess()}

                    {/* Android App Download Banner */}
                    {renderAndroidAppBanner()}
                </div>

            </Main>
        </>
    );
};

export default Dashboard;
